@if (ticket(); as t) {
  <!-- ======== HEADER BANNER ======== -->
  <div
    class="case-header"
    [style.border-left-color]="priorityColor()"
  >
    <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
      <span class="case-id">#{{ t.request_id }}</span>
      <span
        class="badge"
        [class]="priorityBadge()"
      >
        {{ t.priority }}
      </span>
      <span
        class="badge"
        [class]="statusBadge()"
      >
        {{ statusLabel() }}
      </span>
      <span
        class="sla-indicator"
        [style.color]="slaColor()"
      >
        <span
          class="sla-dot"
          [style.background-color]="slaColor()"
        ></span>
        SLA: {{ t.sla_violation_type }}
      </span>
      <span class="ms-auto text-muted small">{{ relativeTime() }}</span>
    </div>
    <h5 class="case-subject mb-0">{{ t.subject }}</h5>
  </div>

  <!-- ======== TWO-COLUMN: Main Content + Sidebar ======== -->
  <div class="case-body">
    <div class="case-layout">
      <!-- MAIN CONTENT — stacked vertically -->
      <div class="case-main">
        <!-- Executive Summary -->
        @if (executiveSummary()) {
          <div class="summary-card mb-3">
            <div class="section-label">Executive Summary</div>
            <p class="summary-text">{{ executiveSummary() }}</p>
          </div>
        }

        <!-- SOC Response Actions (enriched with action buttons) -->
        <!--
          Blast radius of the asset this case is about (ONE-3516).

          Sits above the written summary because it answers the first question
          an analyst asks of a case — what is affected, and how far does it
          reach — which the prose then explains.
        -->
        <app-case-blast-radius
          [resolved]="resolvedOrigins()"
          [reach]="blastReach()"
          [loading]="blastLoading()"
          [reachLoading]="reachLoading()"
          [noOriginReason]="blastEmptyReason()"
          (explore)="onExplore($event)"
        />

        @if (enrichedActions().length === 0 && socResponseNarrative(); as narrative) {
          <!--
            The section had content but nothing parseable as an action. Shown as
            written rather than dropped: an empty SOC Response Actions block
            reads as "no action was taken", which is a statement about the case,
            not about the parser.
          -->
          <div class="action-card mb-3">
            <div class="section-label">SOC Response Actions</div>
            <p class="soc-actions-narrative">{{ narrative }}</p>
          </div>
        }

        @if (enrichedActions().length > 0) {
          <div class="action-card mb-3">
            <div class="section-label">SOC Response Actions</div>
            <div class="soc-actions-list">
              @for (action of enrichedActions(); track action.rawText) {
                <div class="soc-action-item">
                  <div class="soc-action-header">
                    <span class="soc-action-label">{{ action.label }}</span>
                    <span
                      class="soc-action-status"
                      [class]="'status-' + action.status"
                    >
                      {{ action.statusText }}
                    </span>
                  </div>
                  @if (action.hostname) {
                    <div class="soc-action-host">{{ action.hostname }}</div>
                  }

                  <!-- Action buttons / status overlays -->
                  @if (action.status !== 'taken') {
                    <div class="soc-action-buttons">
                      @switch (action.executionState.status) {
                        @case ('idle') {
                          @if (action.availableAction) {
                            <button
                              class="action-btn action-btn-primary"
                              (click)="onTakeAction(action)"
                            >
                              ▶ Take Action
                            </button>
                          }
                          <button
                            class="action-btn action-btn-soc"
                            (click)="onRequestSoc(action)"
                          >
                            Request SOC
                          </button>
                        }
                        @case ('confirming') {
                          <!-- Inline confirmation will be shown via dialog -->
                          <span class="execution-confirming text-muted small">Awaiting confirmation...</span>
                        }
                        @case ('pending') {
                          <span class="execution-pending">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Executing...
                          </span>
                        }
                        @case ('completed') {
                          <span class="execution-completed">Action Completed</span>
                        }
                        @case ('failed') {
                          <span class="execution-failed me-2">Failed</span>
                          <button
                            class="action-btn action-btn-soc"
                            (click)="onRetry(action)"
                          >
                            Retry
                          </button>
                        }
                        @case ('soc_requested') {
                          <span class="execution-soc-requested">SOC Requested</span>
                        }
                      }
                    </div>
                  }
                </div>

                <!-- Confirmation dialog (shown when confirming) -->
                @if (action.executionState.status === 'confirming' && action.availableAction; as avail) {
                  <kendo-dialog
                    title="Confirm: {{ action.label }}"
                    [minWidth]="350"
                    [width]="450"
                    (close)="onCancelAction(action)"
                  >
                    <div class="confirm-body">
                      <p class="mb-2">{{ avail.confirmation_message }}</p>

                      @if (objectKeys(avail.parameters).length > 0) {
                        <div class="confirm-params mb-3">
                          @for (key of objectKeys(avail.parameters); track key) {
                            <span class="badge bg-light text-dark border me-1 mb-1">
                              {{ key }}: {{ avail.parameters[key] }}
                            </span>
                          }
                        </div>
                      }

                      <div class="confirm-warning">
                        <span class="fw-semibold">Warning:</span>
                        This action will be executed immediately and cannot be undone.
                      </div>
                    </div>
                    <kendo-dialog-actions>
                      <button
                        kendoButton
                        class="cyflare-btn-secondary"
                        (click)="onCancelAction(action)"
                      >
                        Cancel
                      </button>
                      <button
                        kendoButton
                        class="cyflare-btn-danger"
                        (click)="onConfirmAction(action)"
                      >
                        Execute
                      </button>
                    </kendo-dialog-actions>
                  </kendo-dialog>
                }
              }
            </div>
          </div>
        }

        <!-- Recommended Remediation -->
        @if (remediation(); as rem) {
          <div class="remediation-card mb-3">
            <div class="section-label">{{ rem.title }}</div>
            @if (rem.actionItems.length > 0) {
              <div class="remediation-items">
                @for (item of rem.actionItems; track item.text) {
                  <div class="remediation-item">
                    <span class="remediation-icon">→</span>
                    <span>{{ item.text }}</span>
                  </div>
                }
              </div>
            } @else {
              <div class="remediation-content">{{ rem.content }}</div>
            }
          </div>
        }

        <!-- Additional parsed sections (Key Details, Threat Intel, IOCs, etc.) -->
        @if (additionalSections().length > 0) {
          <app-parsed-description [parsed]="{ summary: '', sections: additionalSections() }" />
        }
      </div>

      <!-- SIDEBAR -->
      <div class="case-sidebar">
        <!-- Case Details metadata -->
        <div class="sidebar-card mb-3">
          <div class="section-label">Case Details</div>
          @for (row of detailRows(); track row.label; let last = $last) {
            @if (row.value) {
              <div
                class="metadata-row"
                [class.mb-0]="last"
              >
                <div class="metadata-label">{{ row.label }}</div>
                <div class="metadata-value">{{ row.value }}</div>
              </div>
            }
          }
        </div>

        <!-- Talk to SOC -->
        <app-case-soc-button
          [ticket]="t"
          class="d-block mb-3"
        />

        <!-- Timeline -->
        <div class="sidebar-card">
          <div class="section-label">Timeline</div>
          <div class="timeline-item">
            <div class="timeline-dot"></div>
            <div>
              <div class="timeline-label">Created</div>
              <div class="timeline-value">{{ formatDate(t.created_time) }}</div>
            </div>
          </div>
          @if (t.customer_response_time) {
            <div class="timeline-item">
              <div class="timeline-dot dot-info"></div>
              <div>
                <div class="timeline-label">Customer Response</div>
                <div class="timeline-value">{{ formatDate(t.customer_response_time) }}</div>
              </div>
            </div>
          }
          @if (t.ticket_closed_time) {
            <div class="timeline-item">
              <div class="timeline-dot dot-success"></div>
              <div>
                <div class="timeline-label">Closed</div>
                <div class="timeline-value">{{ formatDate(t.ticket_closed_time) }}</div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
